import { useEffect, useRef } from 'react';

/**
 * Google Anti-Gravity Easter Egg
 * Triggered by typing "antigravity" anywhere on the page (or via konami-style sequence)
 * All page elements float to the top with physics simulation.
 */
const AntiGravity = () => {
  const activeRef = useRef(false);
  const typedRef = useRef('');

  useEffect(() => {
    const TRIGGER = 'antigravity';

    const onKeyDown = (e: KeyboardEvent) => {
      typedRef.current = (typedRef.current + e.key).slice(-TRIGGER.length);
      if (typedRef.current === TRIGGER && !activeRef.current) {
        activateAntiGravity();
      }
    };

    const activateAntiGravity = () => {
      activeRef.current = true;
      const elements = Array.from(
        document.querySelectorAll<HTMLElement>(
          'img, h1, h2, h3, h4, p, a, button, span, li, input, div > svg'
        )
      ).filter(el => {
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.top > 0;
      });

      elements.forEach(el => {
        const rect = el.getBoundingClientRect();
        const clone = el.cloneNode(true) as HTMLElement;

        clone.style.cssText = `
          position: fixed;
          left: ${rect.left}px;
          top: ${rect.top}px;
          width: ${rect.width}px;
          height: ${rect.height}px;
          margin: 0;
          z-index: 9999;
          pointer-events: none;
          transition: none;
        `;

        document.body.appendChild(clone);

        let velY = -(Math.random() * 8 + 4);
        let velX = (Math.random() - 0.5) * 4;
        let posX = rect.left;
        let posY = rect.top;
        const gravity = 0.25;
        let rotation = 0;
        const rotSpeed = (Math.random() - 0.5) * 5;

        const animate = () => {
          velY += gravity;
          posX += velX;
          posY += velY;
          rotation += rotSpeed;

          if (posY > window.innerHeight + 200) {
            clone.remove();
            return;
          }

          clone.style.left = `${posX}px`;
          clone.style.top = `${posY}px`;
          clone.style.transform = `rotate(${rotation}deg)`;
          requestAnimationFrame(animate);
        };

        setTimeout(() => requestAnimationFrame(animate), Math.random() * 800);
      });
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return null;
};

export default AntiGravity;
